// client/src/services/auth.ts
import api from '../utils/api';
import axios from 'axios';
import config from '../config/config';

// Create a separate axios instance for auth operations to avoid interceptor loops
const authApi = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

export const login = async (email: string, password: string) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    const { token, refreshToken } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

export const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Validate refresh token format
    if (!refreshToken.match(/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/)) {
      throw new Error('Invalid refresh token format');
    }

    // Helper function for delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    
    // Implement retry logic manually
    let attempts = 0;
    const maxAttempts = 3;
    
    let lastResponse;
    while (attempts < maxAttempts) {
      try {
        lastResponse = await authApi.post('/auth/refresh-token', 
          { refreshToken },
          { timeout: 5000 } // 5 second timeout
        );
        break; // Success, exit the loop
      } catch (error) {
        attempts++;
        if (attempts === maxAttempts) throw error;
        // Exponential backoff: 1s, 2s, 4s
        await delay(Math.pow(2, attempts - 1) * 1000);
      }
    }
    
    if (!lastResponse) {
      throw new Error('Failed to refresh token after all attempts');
    }
    
    const { accessToken, refreshToken: newRefreshToken } = lastResponse.data;
    
    if (!accessToken || !newRefreshToken) {
      
      throw new Error('Invalid response from server');
    }
    
    // Atomic token update
    try {
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('token', accessToken);
    } catch (storageError) {
      // Rollback if storage fails
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      throw new Error('Failed to update tokens');
    }
    
    return {
      accessToken,
      refreshToken: newRefreshToken,
      user: lastResponse.data.user
    };
  } catch (error: any) {
    // Clear tokens on refresh failure
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Enhance error message
    const errorMessage = error.response?.data?.message || error.message || 'Failed to refresh token';
    throw new Error(errorMessage);
  }
};

export const forgotPassword = async (email: string) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (token: string, password: string) => {
  try {
    const response = await api.post(`/auth/reset-password/${token}`, { password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
  try {
    const response = await api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  } catch (error) {
    throw error;
  }
};